/**
 * AppTopBar — slim header for all non-console pages.
 *
 * Left:   page title (derived from route)
 * Right:  project selector + account button
 */
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import ProjectSelector from "@/components/ProjectSelector";
import { useAuth } from "@/auth/AuthProvider";

const ROUTE_TITLES = [
    { match: /^\/projects\/[^/]+$/, section: "Projects", label: "Project" },
    { match: /^\/projects$/,         section: "Projects", label: null },
    { match: /^\/conversations/,     section: "Conversations", label: null },
    { match: /^\/memory/,            section: "Memory", label: null },
    { match: /^\/decisions/,         section: "Decisions", label: null },
    { match: /^\/ideas/,             section: "Ideas", label: null },
    { match: /^\/tasks/,             section: "Tasks", label: null },
    { match: /^\/settings\/[^/]+$/,  section: "Settings", label: "detail" },
    { match: /^\/settings/,          section: "Settings", label: null },
];

function useTitle() {
    const { pathname } = useLocation();
    const hit = ROUTE_TITLES.find((r) => r.match.test(pathname));
    return hit ? { section: hit.section, sub: hit.label } : { section: "Qoryn", sub: null };
}

export default function AppTopBar() {
    const { section, sub } = useTitle();
    const navigate = useNavigate();
    const { user } = useAuth();
    return (
        <header
            data-testid="app-top-bar"
            className="sticky top-0 z-20 flex items-center gap-4 px-6 md:px-10 h-14 border-b border-qoryn-border/50 bg-qoryn-bg/80 backdrop-blur-xl"
        >
            <div className="flex items-center gap-2 min-w-0 flex-1">
                <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-qoryn-textDim hover:text-qoryn-cyan transition-colors qoryn-focus"
                    data-testid="app-brand"
                    title="Founder Console"
                >
                    Qoryn
                </button>
                <ChevronRight className="h-3 w-3 text-qoryn-textMuted" />
                <span
                    className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-qoryn-text"
                    data-testid="app-section"
                >
                    {section}
                </span>
                {sub && (
                    <>
                        <ChevronRight className="h-3 w-3 text-qoryn-textMuted" />
                        <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-qoryn-textDim">
                            {sub}
                        </span>
                    </>
                )}
            </div>
            <ProjectSelector />
            <button
                type="button"
                data-testid="app-account-btn"
                onClick={() => navigate("/settings/account")}
                title={user?.email ?? "Account"}
                className="qoryn-panel rounded-full h-8 px-3 flex items-center gap-2 hover:bg-white/[0.04] transition-colors qoryn-focus"
            >
                <span className="h-1.5 w-1.5 rounded-full bg-qoryn-cyan" style={{ boxShadow: "0 0 6px #00E5FF" }} />
                <span className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-text truncate max-w-[160px]">
                    {(user?.email || "account").split("@")[0]}
                </span>
            </button>
        </header>
    );
}
